import { Form, Link, useFetcher, useMatches, useSubmit } from '@remix-run/react'
import { useRef } from 'react'
import { useOptimisticThemeMode } from '#app/root.js'
import { type action } from '#app/routes/_app+/settings+/theme.js'
import { type Theme } from '#app/utils/theme.server.js'
import { type useOptionalUser } from '#app/utils/user.js'
import { SearchBar } from './search-bar'
import { Button } from './ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx'
import { Icon } from './ui/icon'

export function AppHeader({
	user,
	theme,
}: {
	user: ReturnType<typeof useOptionalUser>
	theme: Theme | null
}) {
	const isAdmin = !!user?.roles.find(r => r.name === 'admin')
	const matches = useMatches()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	const themeRef = useRef<HTMLFormElement>(null)
	const fetcher = useFetcher<typeof action>()
	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? theme ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const nextItem =
		nextMode === 'system' ? 'laptop' : nextMode === 'dark' ? 'moon' : 'sun'
	const isOnSearchPage = matches.find(
		m => m.id === 'routes/app+/library+/index',
	)
	const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />
	return (
		<header
			className={`relative z-30 flex h-16 w-full items-center justify-between border-b border-b-border bg-background px-3`}
		>
			<nav className="flex gap-3">
				<a
					href="https://kogia.org"
					target="_blank"
					className="ml-1 mr-3 block outline-offset-8 outline-ring"
					rel="noreferrer"
				>
					<img
						className="h-10 dark:hidden"
						src="/logos/kogia-logo-light-theme-compressed.png"
						alt="Kogia Logo"
					/>
					<img
						className="hidden h-10 dark:block"
						src="/logos/kogia-logo-dark-theme-compressed.png"
						alt="Kogia Logo"
					/>
				</a>
				<Button
					className="hidden md:flex"
					asChild
					variant={'ghost'}
					size="default"
				>
					<Link to="/">
						<Icon className="mr-2" name="magnifying-glass" size="md" />
						Library
					</Link>
				</Button>

				<Button
					className="hidden md:flex"
					asChild
					variant={'ghost'}
					size="default"
				>
					<Link to="/contributors">Contributors</Link>
				</Button>
				<Button
					className="hidden lg:flex"
					asChild
					variant={'ghost'}
					size="default"
				>
					<a
						href="https://www.kogia.org/studio"
						// target="_blank"
						// rel="noreferrer"
					>
						Studio
					</a>
				</Button>
				<Button
					className="hidden md:flex"
					asChild
					variant={'ghost'}
					size="default"
				>
					<a
						href="https://www.kogia.org/about"
						// target="_blank"
						// rel="noreferrer"
					>
						About
					</a>
				</Button>
				<Button
					className="hidden md:flex"
					asChild
					variant={'ghost'}
					size="default"
				>
					<a
						href="https://www.kogia.org/donate"
						// target="_blank"
						// rel="noreferrer"
					>
						Donate
					</a>
				</Button>
			</nav>
			<div className="flex flex-1 justify-end gap-2">
				{user && (
					<div className="hidden max-w-xl flex-1 lg:block">{searchBar}</div>
				)}
				{!user && (
					<div className="flex gap-3">
						<Button
							className="hidden md:flex"
							asChild
							variant={'ghost'}
							size="default"
						>
							<Link to="/login">Login</Link>
						</Button>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline">
									<Icon
										name="hamburger-menu"
										className="flex text-body-md md:hidden"
									>
										<span className="sr-only">Menu</span>
									</Icon>
									<span className="hidden md:block">Request Access</span>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuPortal>
								<DropdownMenuContent sideOffset={8} align="end">
									<DropdownMenuItem asChild>
										<Link className="md:hidden" prefetch="intent" to={`/`}>
											<Icon size="md" name="magnifying-glass">
												Library
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link
											className="md:hidden"
											prefetch="intent"
											to={`/contributors`}
										>
											<Icon size="md" name="camera">
												Contributors
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuSeparator className="md:hidden" />
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/apply`}>
											<Icon size="md" name="avatar">
												Apply as a Member
											</Icon>
										</Link>
									</DropdownMenuItem>
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/contributor/apply`}>
											<Icon size="md" name="video">
												Apply as a Contributor
											</Icon>
										</Link>
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenuPortal>
						</DropdownMenu>
					</div>
				)}
				{user && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button size="icon" variant="outline">
								<Icon name="hamburger-menu" className="text-body-md">
									<span className="sr-only">Menu</span>
								</Icon>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuPortal>
							<DropdownMenuContent sideOffset={8} align="end">
								<DropdownMenuItem className="flex md:hidden" asChild>
									<Link prefetch="intent" to={`/`}>
										<Icon size="md" name="magnifying-glass">
											Library
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem className="flex md:hidden" asChild>
									<Link prefetch="intent" to={`/contributors`}>
										<Icon size="md" name="video">
											Contributors
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator className="flex md:hidden" />
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) && (
									<DropdownMenuItem asChild>
										<Link
											prefetch="intent"
											to={`/contributors/${user?.username}`}
										>
											<Icon size="md" name="avatar">
												Your Profile
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/collections`}>
										<Icon size="md" name="layers">
											Your Collections
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) && (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/settings/bulk-upload/photo`}>
											<Icon size="md" name="camera">
												Upload Photos
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'video',
								) && (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/settings/bulk-upload/video`}>
											<Icon size="md" name="video">
												Upload Videos
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/collections/new`}>
										<Icon size="md" name="layers">
											Create Collection
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem asChild>
									<Link prefetch="intent" to={`/settings/profile`}>
										<Icon size="md" name="pencil-2">
											Edit Profile
										</Icon>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										prefetch="intent"
										to={`/settings/profile`}
										className="pr-2"
									>
										<Icon size="md" name="gear">
											Settings
										</Icon>
									</Link>
								</DropdownMenuItem>
								{isAdmin && (
									<>
										<DropdownMenuItem asChild>
											<Link prefetch="intent" to={`/admin`} className="pr-2">
												<Icon size="md" name="lock-closed">
													Admin
												</Icon>
											</Link>
										</DropdownMenuItem>
									</>
								)}
								{user.roles.find(
									r => r.name.toLocaleLowerCase() === 'image',
								) ? (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/tos/contributors`}>
											<Icon size="md" name="book">
												Terms of Service
											</Icon>
										</Link>
									</DropdownMenuItem>
								) : (
									<DropdownMenuItem asChild>
										<Link prefetch="intent" to={`/tos/members`}>
											<Icon size="md" name="book">
												Terms of Service
											</Icon>
										</Link>
									</DropdownMenuItem>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem
									asChild
									// this prevents the menu from closing before the form submission is completed
									onSelect={event => {
										event.preventDefault()
										submit(formRef.current)
									}}
								>
									<Form action="/logout" method="POST" ref={formRef}>
										<Icon size="md" name="exit">
											<button type="submit">Logout</button>
										</Icon>
									</Form>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									asChild
									onSelect={event => {
										event.preventDefault()
									}}
								>
									<fetcher.Form action="/" method="POST" ref={themeRef}>
										<input type="hidden" name="theme" value={nextMode} />
										<Icon size="md" name={nextItem}>
											<button type="submit">
												{nextMode === 'dark' && 'Night Theme'}
												{nextMode === 'light' && 'Day Theme'}
												{nextMode === 'system' && 'System Default'}
											</button>
										</Icon>
									</fetcher.Form>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenuPortal>
					</DropdownMenu>
				)}
			</div>
		</header>
	)
}
